import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import {
  faBook,
  faCompress,
  faExpand,
  faFileAlt,
  faPaperclip,
  faPaperPlane,
  faStop,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import A2uiSurface from 'components/upb_viewer_beta/copilot/A2uiSurface';
import PlainButton from 'components/upb_viewer_beta/copilot/components/PlainButton';
import WikiMenuItem from 'components/upb_viewer_beta/copilot/components/WikiMenuItem';
import {
  createReport,
  fetchWikis,
  getReportStatus,
  sendMessageStream,
  wikiUrl,
} from 'components/upb_viewer_beta/copilot/copilotApi';
import {
  PERSONAS,
  REPORT_GEN_STATUS,
  REPORT_POLL_INTERVAL_MS,
  REPORT_POLL_MAX,
  REPORT_STATES,
} from 'components/upb_viewer_beta/copilot/constants';
import Markdown from 'components/upb_viewer_beta/copilot/Markdown';
import {
  ACCENT_VIVID,
  ACCENT_VIVID_DARK,
  BG_1,
  BG_2,
  BG_3,
  BORDER_1,
  FG_1,
  FG_2,
  FG_3,
  FG_INV,
  FONT_BODY,
  FONT_SIZE_BODY,
  FONT_SIZE_SMALL,
  FONT_SIZE_XS,
  FONT_TITLE,
  FONT_WEIGHT_MEDIUM,
  FONT_WEIGHT_SEMIBOLD,
  PRIMARY_LIGHT_RGBA_14,
  RADIUS_FULL,
  RADIUS_MD,
  RADIUS_SM,
  SPACE_2,
  SPACE_3,
  SPACE_4,
  SUCCESS_COLOR,
  WARNING_COLOR,
  WHITE,
} from 'components/upb_viewer_beta/css/betaTokens';

const INPUT_MAX_H = 140; // textarea grows with content up to here, then scrolls
const ACCEPT_TYPES =
  'image/png,image/jpeg,image/gif,image/webp,application/pdf,' +
  'text/plain,text/csv,application/json,.md,.tsv,.log';

const GREETING = {
  role: 'assistant',
  text:
    "Hi — I'm Marco, your product-catalog guide. Ask me about products, manufacturers, " +
    'brands, categories, or data quality in the catalog.',
  traces: null,
  done: true,
};

/**
 * FsaCopilot — a docked, machan-style chat assistant for the UPB Beta view.
 * A floating launcher opens a right-side slide-out panel. Streams replies and shows an
 * expandable "what I ran" trace (the tool/SQL used) so answers are transparent.
 *
 * Backend is mocked locally for now via copilotApi.sendMessageStream — swap that for the
 * real FSA Copilot service later without touching this component.
 */
export default function FsaCopilot() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [persona, setPersona] = useState('business'); // 'business' | 'technical'
  // Attachment is ephemeral: held only in this component's memory, sent inline with the message,
  // never uploaded/stored. Cleared on remove or when the panel closes — nothing piles up.
  const [attachment, setAttachment] = useState(null); // {kind, name, media_type, data|text} | null
  const [attachErr, setAttachErr] = useState('');
  const [preview, setPreview] = useState(null); // image attachment shown full-size in a lightbox
  const [wikis, setWikis] = useState(null); // knowledge index, lazy-loaded on first open
  const [wikiMenuOpen, setWikiMenuOpen] = useState(false);
  const cancelRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  // Auto-grow the textarea with its content (up to INPUT_MAX_H, then it scrolls).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, INPUT_MAX_H)}px`;
  }, [input, open]);

  useEffect(() => () => cancelRef.current && cancelRef.current(), []);

  // Close the image lightbox on Escape.
  useEffect(() => {
    if (!preview) return undefined;
    const onKey = e => e.key === 'Escape' && setPreview(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview]);

  const runSend = raw => {
    const text = (raw || '').trim();
    const att = attachment; // for THIS message only; composer is cleared after send (one-shot)
    if ((!text && !att) || busy) return;
    // Prior completed turns (text only) give the backend conversational continuity for
    // follow-ups like "show me which I can get from birite". Empty/in-flight turns are skipped.
    // Carry prior turns (text + any attachment) so a file stays available for follow-ups,
    // even though the composer is one-shot. Include attachment-only user turns too.
    const history = messages
      .filter(msg => msg.done && (msg.text || msg.attachment))
      .map(msg => ({ role: msg.role, text: msg.text, attachment: msg.attachment || null }));
    setInput('');
    setBusy(true);
    setMessages(m => [
      ...m,
      { role: 'user', text, attachment: att, done: true },
      { role: 'assistant', text: '', traces: null, components: null, done: false },
    ]);

    cancelRef.current = sendMessageStream(
      text,
      {
        onToken: tok =>
          setMessages(m => {
            const next = m.slice();
            const last = next[next.length - 1];
            next[next.length - 1] = { ...last, text: last.text + tok };
            return next;
          }),
        onDone: meta => {
          setMessages(m => {
            const next = m.slice();
            const last = next[next.length - 1];
            next[next.length - 1] = {
              ...last,
              traces: (meta && meta.traces) || [],
              components: (meta && meta.components) || [],
              suggestions: (meta && meta.suggestions) || [],
              done: true,
            };
            return next;
          });
          setBusy(false);
        },
      },
      history,
      persona,
      att,
    );
    // One-shot: the attachment belongs to the message just sent — clear the composer so it
    // isn't silently re-attached to the next question (ChatGPT-style).
    setAttachment(null);
    setAttachErr('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const send = () => runSend(input);

  // --- Attachments (ephemeral) -------------------------------------------------
  const clearAttachment = () => {
    setAttachment(null);
    setAttachErr('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const closePanel = () => {
    setOpen(false);
    setWikiMenuOpen(false);
    clearAttachment(); // ephemeral: dropping the panel drops the file — nothing persists
  };

  // Knowledge menu: lazy-load the wiki index on first open, then toggle the dropdown.
  const toggleWikiMenu = () => {
    setWikiMenuOpen(o => !o);
    if (wikis === null) fetchWikis().then(setWikis);
  };

  const openWiki = slug => {
    window.open(wikiUrl(slug), '_blank', 'noopener'); // self-contained, session-gated HTML page
    setWikiMenuOpen(false);
  };

  // Read a File (from the picker OR a clipboard paste) into an ephemeral attachment.
  const ingestFile = file => {
    if (!file) return;
    setAttachErr('');
    const mt = file.type || '';
    // pasted blobs often have no filename — synthesize one from the mime subtype
    const name = file.name || `pasted-image.${mt.split('/')[1] || 'png'}`;
    const isImage = /^image\/(png|jpe?g|gif|webp)$/.test(mt);
    const isPdf = mt === 'application/pdf' || /\.pdf$/i.test(name);
    const isText =
      mt.startsWith('text/') ||
      /\.(csv|txt|json|md|tsv|log)$/i.test(name) ||
      mt === 'application/json';
    const cap = isImage ? 5 : isPdf ? 10 : 1; // MB
    if (file.size > cap * 1024 * 1024) {
      setAttachErr(`${name} is too large (max ${cap} MB).`);
      return;
    }
    const reader = new FileReader();
    if (isImage || isPdf) {
      reader.onload = () => {
        const data = String(reader.result).split(',')[1] || ''; // strip "data:...;base64,"
        setAttachment({
          kind: isImage ? 'image' : 'document',
          name,
          media_type: isImage ? mt : 'application/pdf',
          data,
        });
      };
      reader.onerror = () => setAttachErr('Could not read that file.');
      reader.readAsDataURL(file);
    } else if (isText) {
      reader.onload = () => setAttachment({ kind: 'text', name, text: String(reader.result) });
      reader.onerror = () => setAttachErr('Could not read that file.');
      reader.readAsText(file);
    } else {
      setAttachErr('Unsupported file. Use an image, PDF, or text/CSV file.');
    }
  };

  const onPickFile = e => {
    const file = e.target.files && e.target.files[0];
    if (fileRef.current) fileRef.current.value = ''; // allow re-picking the same file
    ingestFile(file);
  };

  // Paste (Cmd/Ctrl+V) an image or file into the composer to attach it; plain text pastes normally.
  const onPaste = e => {
    const items = (e.clipboardData && e.clipboardData.items) || [];
    for (let i = 0; i < items.length; i += 1) {
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile();
        if (file) {
          e.preventDefault(); // it's a file, not text — don't dump blob junk into the textarea
          ingestFile(file);
          return;
        }
      }
    }
  };

  // Stop the in-flight answer: abort the stream and finalize the partial message in place.
  const stop = () => {
    if (cancelRef.current) cancelRef.current();
    cancelRef.current = null;
    setBusy(false);
    setMessages(m => {
      const next = m.slice();
      const last = next[next.length - 1];
      if (last && last.role === 'assistant' && !last.done) {
        next[next.length - 1] = { ...last, text: last.text || 'Stopped.', done: true };
      }
      return next;
    });
  };

  // Button components can drive the conversation (send_message) or deep-link (navigate).
  const handleAction = ({ name, context }) => {
    if (name === 'send_message' && context && context.message) {
      runSend(context.message);
    } else if (name === 'navigate' && context && context.url) {
      window.location.assign(context.url);
    }
  };

  const onKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Suggested follow-up chips belong to the latest completed answer (they vanish on a new turn).
  const lastMsg = messages[messages.length - 1];
  const suggestions =
    lastMsg && lastMsg.role === 'assistant' && lastMsg.done && Array.isArray(lastMsg.suggestions)
      ? lastMsg.suggestions
      : [];

  return (
    <>
      {!open && (
        <PlainButton
          className={css(styles.launcher)}
          onClick={() => setOpen(true)}
          ariaLabel="Open Marco"
        >
          <MarcoMark size={26} />
        </PlainButton>
      )}

      <div
        className={css(
          styles.panel,
          open ? styles.panelOpen : styles.panelClosed,
          expanded && styles.panelExpanded,
        )}
      >
        <div className={css(styles.header)}>
          <div className={css(styles.headerTitle)}>
            <span className={css(styles.dot)} />
            Marco
            <span className={css(styles.beta)}>beta</span>
          </div>
          <div className={css(styles.headerActions)}>
            <div className={css(styles.persona)} role="group" aria-label="Answer detail">
              <PlainButton
                className={css(
                  styles.personaOpt,
                  persona === PERSONAS.BUSINESS && styles.personaOptOn,
                )}
                onClick={() => setPersona(PERSONAS.BUSINESS)}
                title="Plain outcome numbers for business users"
              >
                Business
              </PlainButton>
              <PlainButton
                className={css(
                  styles.personaOpt,
                  persona === PERSONAS.TECHNICAL && styles.personaOptOn,
                )}
                onClick={() => setPersona(PERSONAS.TECHNICAL)}
                title="Include schema, flags and query detail"
              >
                Technical
              </PlainButton>
            </div>
            <div className={css(styles.wikiWrap)}>
              <PlainButton
                className={css(styles.iconBtn, wikiMenuOpen && styles.iconBtnOn)}
                onClick={toggleWikiMenu}
                ariaLabel="Knowledge wikis"
                title="Knowledge wikis"
              >
                <FontAwesomeIcon icon={faBook} />
              </PlainButton>
              {wikiMenuOpen && (
                <div className={css(styles.wikiMenu)} role="menu">
                  <div className={css(styles.wikiMenuHead)}>Knowledge</div>
                  {wikis === null && <div className={css(styles.wikiEmpty)}>Loading…</div>}
                  {wikis !== null && wikis.length === 0 && (
                    <div className={css(styles.wikiEmpty)}>No wikis available.</div>
                  )}
                  {(wikis || []).map(w => (
                    <WikiMenuItem
                      key={w.slug}
                      itemClassName={css(styles.wikiItem)}
                      titleClassName={css(styles.wikiItemTitle)}
                      descClassName={css(styles.wikiItemDesc)}
                      title={w.title}
                      description={w.description}
                      onClick={() => openWiki(w.slug)}
                    />
                  ))}
                </div>
              )}
            </div>
            <PlainButton
              className={css(styles.iconBtn)}
              onClick={() => setExpanded(e => !e)}
              ariaLabel={expanded ? 'Restore size' : 'Expand to half screen'}
              title={expanded ? 'Restore' : 'Expand'}
            >
              <FontAwesomeIcon icon={expanded ? faCompress : faExpand} />
            </PlainButton>
            <PlainButton
              className={css(styles.iconBtn, styles.iconBtnClose)}
              onClick={closePanel}
              ariaLabel="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </PlainButton>
          </div>
        </div>

        <div className={css(styles.messages)} ref={listRef}>
          {messages.map((m, idx) => (
            <Message
              key={idx}
              m={m}
              onAction={handleAction}
              expanded={expanded}
              onPreview={setPreview}
              question={idx > 0 && messages[idx - 1].role === 'user' ? messages[idx - 1].text : ''}
            />
          ))}
        </div>

        {!busy && suggestions.length > 0 && (
          <div className={css(styles.suggestRow)}>
            {suggestions.map((q, i) => (
              <PlainButton
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                className={css(styles.suggestChip)}
                onClick={() => runSend(q)}
                title={q}
              >
                {q}
              </PlainButton>
            ))}
          </div>
        )}

        <div className={css(styles.inputBar)}>
          {attachErr ? (
            <div className={css(styles.attachErr)}>{attachErr}</div>
          ) : (
            attachment && <AttachmentChip att={attachment} onRemove={clearAttachment} />
          )}
          <div className={css(styles.inputRow)}>
            <input
              type="file"
              ref={fileRef}
              onChange={onPickFile}
              accept={ACCEPT_TYPES}
              style={{ display: 'none' }}
            />
            <PlainButton
              className={css(styles.attachBtn, attachment && styles.attachBtnOn)}
              onClick={() => fileRef.current && fileRef.current.click()}
              disabled={busy}
              ariaLabel="Attach a file or photo"
              title="Attach an image, PDF, or text file — removed when you close Marco"
            >
              <FontAwesomeIcon icon={faPaperclip} />
            </PlainButton>
            <textarea
              ref={inputRef}
              className={css(styles.input)}
              placeholder="Ask about products, manufacturers, categories…"
              value={input}
              rows={1}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
            />
            {busy ? (
              <PlainButton
                className={css(styles.sendBtn)}
                onClick={stop}
                ariaLabel="Stop"
                title="Stop"
              >
                <FontAwesomeIcon icon={faStop} style={{ color: WHITE }} />
              </PlainButton>
            ) : (
              <PlainButton
                className={css(
                  styles.sendBtn,
                  !input.trim() && !attachment && styles.sendBtnDisabled,
                )}
                onClick={send}
                disabled={!input.trim() && !attachment}
                ariaLabel="Send"
              >
                <FontAwesomeIcon icon={faPaperPlane} style={{ color: WHITE }} />
              </PlainButton>
            )}
          </div>
        </div>
      </div>

      {preview && preview.data && (
        <div
          className={css(styles.lightbox)}
          onClick={() => setPreview(null)}
          role="dialog"
          aria-modal="true"
        >
          <PlainButton
            className={css(styles.lightboxClose)}
            onClick={() => setPreview(null)}
            ariaLabel="Close preview"
          >
            <FontAwesomeIcon icon={faTimes} />
          </PlainButton>
          {/* stop bubbling so clicking the image itself doesn't close the overlay */}
          <img
            className={css(styles.lightboxImg)}
            src={`data:${preview.media_type};base64,${preview.data}`}
            alt={preview.name}
            onClick={e => e.stopPropagation()}
          />
          <div className={css(styles.lightboxCaption)}>{preview.name}</div>
        </div>
      )}
    </>
  );
}

// Claude-style "thinking" line: a single word with a light sweep, cycling through phrases while
// Marco works (shown before the first token streams in) instead of a bare loading dot.
const THINKING_PHRASES = [
  'Thinking',
  'Looking through the catalog',
  'Checking the numbers',
  'Piecing it together',
  'Almost there',
];
function ThinkingLabel() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI(n => (n + 1) % THINKING_PHRASES.length), 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <span key={i} className={css(styles.thinkingWord)}>
      {THINKING_PHRASES[i]}…
    </span>
  );
}

function Message({ m, onAction, expanded, onPreview, question }) {
  const isUser = m.role === 'user';
  // Every tool call is transparency, shown only behind the collapsed "Ran N lookups" fold.
  // The visible answer is prose + the agent-declared A2UI components — never raw tool data.
  const traces = (m.traces || []).filter(t => t && (t.detail || (t.data && t.data.length)));
  const components = m.components || [];
  // The actual query result rows behind the answer — Claude uses these to design the report.
  const datasets = traces
    .filter(
      t => Array.isArray(t.columns) && t.columns.length && Array.isArray(t.data) && t.data.length,
    )
    .map(t => ({ label: t.detail || t.tool, columns: t.columns, rows: t.data.slice(0, 300) }));
  const thinking = !isUser && !m.text && !m.done && components.length === 0;
  return (
    <div className={css(styles.row, isUser ? styles.rowUser : styles.rowBot)}>
      {!isUser && (
        <div className={css(styles.avatar)} aria-hidden="true">
          <MarcoMark size={16} />
        </div>
      )}
      <div
        className={css(
          styles.bubble,
          isUser ? styles.bubbleUser : styles.bubbleBot,
          !isUser && components.length > 0 && styles.bubbleWide,
        )}
      >
        {thinking ? (
          <ThinkingLabel />
        ) : /* assistant prose is light markdown (bold/lists/code); user text stays literal */
        isUser ? (
          <>
            {m.attachment &&
              (m.attachment.kind === 'image' && m.attachment.data ? (
                <button
                  type="button"
                  className={css(styles.bubbleImgBtn)}
                  onClick={() => onPreview && onPreview(m.attachment)}
                  title="Click to preview"
                >
                  <img
                    className={css(styles.bubbleImg)}
                    src={`data:${m.attachment.media_type};base64,${m.attachment.data}`}
                    alt={m.attachment.name}
                  />
                </button>
              ) : (
                <AttachmentChip att={m.attachment} onBubble={true} />
              ))}
            {m.text}
          </>
        ) : (
          <Markdown text={m.text} />
        )}
        {!isUser && components.length > 0 && (
          <A2uiSurface components={components} onAction={onAction} expanded={expanded} />
        )}
        {traces.length > 0 && <ExploredTraces traces={traces} />}
        {/* A real answer (charts and/or a tool ran) can be turned into a shareable HTML page. */}
        {!isUser && m.done && (components.length > 0 || traces.length > 0) && (
          <ReportControl
            title={deriveTitle(m.text)}
            question={question}
            narrative={m.text || ''}
            components={components}
            datasets={datasets}
          />
        )}
      </div>
    </div>
  );
}

/** A concise, markdown-free title from the answer (first heading, else first sentence). */
function deriveTitle(text) {
  if (!text) return 'Marco report';
  const heading = text.match(/^#{1,3}\s+(.+)$/m);
  const raw = (heading ? heading[1] : text.split('\n').find(l => l.trim()) || '')
    .replace(/[*_`#>]/g, '') // strip markdown markers so they don't show literally
    .replace(/\s+/g, ' ')
    .trim();
  const dot = raw.indexOf('. ');
  let title = dot > 12 ? raw.slice(0, dot) : raw; // prefer just the first sentence
  if (title.length > 90) title = `${title.slice(0, 90).replace(/\s+\S*$/, '')}…`;
  return title || 'Marco report';
}

/** "Create shareable report" button that becomes a ReportCard (Open ↗ / Copy link) once built. */
function ReportControl({ title, question, narrative, components, datasets }) {
  const [state, setState] = useState(REPORT_STATES.IDLE);
  const [report, setReport] = useState(null);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null); // pending status-poll timer, cleared on unmount
  const cancelledRef = useRef(false);

  // Stop polling if the component unmounts mid-generation.
  useEffect(
    () => () => {
      cancelledRef.current = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    },
    [],
  );

  const make = () => {
    cancelledRef.current = false;
    setState(REPORT_STATES.BUSY);
    const done = r => {
      if (cancelledRef.current) return;
      setReport(r);
      setState(REPORT_STATES.DONE);
    };
    const fail = () => {
      if (!cancelledRef.current) setState(REPORT_STATES.ERROR);
    };
    createReport({ title, question, narrative, components, datasets }).then(r => {
      if (cancelledRef.current) return;
      if (r.status === REPORT_GEN_STATUS.READY) {
        done(r);
        return;
      }
      // The report is authored off-request; keep the "Designing report…" state and poll its status
      // until the HTML is ready (bounded, so a stuck job surfaces as an error rather than hanging).
      let attempts = 0;
      const poll = () => {
        if (cancelledRef.current) return;
        getReportStatus(r.id).then(s => {
          if (cancelledRef.current) return;
          if (s.status === REPORT_GEN_STATUS.READY) {
            done(r);
          } else if (s.status === REPORT_GEN_STATUS.PENDING && attempts < REPORT_POLL_MAX) {
            attempts += 1;
            pollRef.current = setTimeout(poll, REPORT_POLL_INTERVAL_MS);
          } else {
            fail(); // invalid / expired / timed out
          }
        }, fail);
      };
      pollRef.current = setTimeout(poll, REPORT_POLL_INTERVAL_MS);
    }, fail);
  };

  const copy = () => {
    if (report && report.url && navigator.clipboard) {
      navigator.clipboard.writeText(report.url).then(
        () => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        },
        () => {},
      );
    }
  };

  if (state === REPORT_STATES.DONE && report) {
    return (
      <div className={css(styles.reportCard)}>
        <div className={css(styles.reportHead)}>
          <span className={css(styles.reportReady)}>Report ready</span>
          {report.expires_on && (
            <span className={css(styles.reportExpiry)}>Expires {report.expires_on}</span>
          )}
        </div>
        <div className={css(styles.reportActions)}>
          <a
            className={css(styles.reportOpen)}
            href={report.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open ↗
          </a>
          <PlainButton className={css(styles.reportCopy)} onClick={copy}>
            {copied ? 'Copied' : 'Copy link'}
          </PlainButton>
        </div>
      </div>
    );
  }

  return (
    <PlainButton
      className={css(styles.reportBtn)}
      onClick={make}
      disabled={state === REPORT_STATES.BUSY}
    >
      <FontAwesomeIcon icon={faFileAlt} />
      {state === REPORT_STATES.BUSY
        ? 'Designing report…'
        : state === REPORT_STATES.ERROR
        ? 'Could not create — retry'
        : 'Create shareable report'}
    </PlainButton>
  );
}
ReportControl.propTypes = {
  title: PropTypes.string,
  question: PropTypes.string,
  narrative: PropTypes.string,
  components: PropTypes.arrayOf(PropTypes.object),
  datasets: PropTypes.arrayOf(PropTypes.object),
};
ReportControl.defaultProps = {
  title: '',
  question: '',
  narrative: '',
  components: [],
  datasets: [],
};

Message.propTypes = {
  m: PropTypes.shape({
    role: PropTypes.string,
    text: PropTypes.string,
    done: PropTypes.bool,
    traces: PropTypes.arrayOf(PropTypes.object),
    components: PropTypes.arrayOf(PropTypes.object),
    attachment: PropTypes.object,
  }).isRequired,
  onAction: PropTypes.func,
  expanded: PropTypes.bool,
  onPreview: PropTypes.func,
  question: PropTypes.string,
};

Message.defaultProps = { onAction: null, expanded: false, onPreview: null, question: '' };

/** Compact attachment pill — image thumbnail or a file glyph + name. Used in the input bar
 * (with a remove ✕) and inside the user's message bubble (onBubble). */
function AttachmentChip({ att, onRemove, onBubble }) {
  const isImg = att.kind === 'image' && att.data;
  return (
    <div className={css(styles.chip, onBubble && styles.chipOnBubble)}>
      {isImg ? (
        <img
          className={css(styles.chipThumb)}
          src={`data:${att.media_type};base64,${att.data}`}
          alt={att.name}
        />
      ) : (
        <span className={css(styles.chipIcon)}>{att.kind === 'document' ? '📄' : '📎'}</span>
      )}
      <span className={css(styles.chipName)}>{att.name}</span>
      {onRemove && (
        <PlainButton
          className={css(styles.chipRemove)}
          onClick={onRemove}
          ariaLabel="Remove attachment"
        >
          ✕
        </PlainButton>
      )}
    </div>
  );
}
AttachmentChip.propTypes = {
  att: PropTypes.object.isRequired,
  onRemove: PropTypes.func,
  onBubble: PropTypes.bool,
};
AttachmentChip.defaultProps = { onRemove: null, onBubble: false };

const TOOL_LABEL = {
  run_sql: 'SQL query',
  vector_search: 'semantic search',
  graph_traverse: 'graph traversal',
  column_blast_radius: 'blast radius',
  describe_object: 'object lineage',
  fsa_data_model: 'data-model lookup',
  cnd_data_model: 'data-model lookup',
  code_search: 'code search',
  code_impact: 'code impact',
  code_trace: 'code trace',
  code_context: 'code context',
  // discovered gitnexus tools carry their real names (the code_* names are the fallback set)
  context: 'code context',
  impact: 'code impact',
  query: 'code search',
  trace: 'code trace',
};

/** One transparency line: the tool that ran + its query/detail. No chart — this is "what I ran". */
function TraceLine({ trace }) {
  const label = TOOL_LABEL[trace.tool] || trace.tool || 'lookup';
  const failed = trace.ok === false || trace.error;
  // Prose tools (blast radius, lineage, code, data-model) return analysis, not rows — so we don't
  // show a "0 rows" count that reads like nothing was found.
  const analysis = trace.kind === 'analysis';
  const rows = typeof trace.rows === 'number' ? trace.rows : null;
  return (
    <div className={css(styles.traceItem)}>
      <div className={css(styles.traceHead)}>
        <span className={css(failed ? styles.traceFail : styles.traceOk)}>
          {failed ? '⚠' : '✓'}
        </span>
        Ran {label}
        {!failed && analysis && (
          <span className={css(styles.traceMeta)}>{' · '}returned analysis</span>
        )}
        {!failed && !analysis && rows !== null && (
          <span className={css(styles.traceMeta)}>
            {' · '}
            {rows.toLocaleString('en-US')} row{rows === 1 ? '' : 's'}
          </span>
        )}
      </div>
      {failed && trace.error && <div className={css(styles.traceErr)}>{trace.error}</div>}
      {trace.detail && <pre className={css(styles.tracePre)}>{trace.detail}</pre>}
    </div>
  );
}

TraceLine.propTypes = { trace: PropTypes.object.isRequired };

/** Collapses the agent's lookups behind one line so only the answer shows. */
function ExploredTraces({ traces }) {
  const [show, setShow] = useState(false);
  return (
    <div className={css(styles.explored)}>
      <PlainButton className={css(styles.exploredToggle)} onClick={() => setShow(s => !s)}>
        <span className={css(styles.exploredChev)}>{show ? '▾' : '▸'}</span>
        Ran {traces.length} lookup{traces.length === 1 ? '' : 's'} to get here
      </PlainButton>
      {show &&
        traces.map((t, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <TraceLine key={i} trace={t} />
        ))}
    </div>
  );
}

ExploredTraces.propTypes = {
  traces: PropTypes.arrayOf(PropTypes.object).isRequired,
};

/* ---- inline icons (no extra deps) ---- */
// Marco's mark: a compass — he's the explorer that finds things in the catalog.
const MarcoMark = ({ size }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={WHITE}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9.5" />
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88" fill={WHITE} stroke="none" />
    <circle cx="12" cy="12" r="1.1" fill={ACCENT_VIVID_DARK} stroke="none" />
  </svg>
);
MarcoMark.propTypes = { size: PropTypes.number };
MarcoMark.defaultProps = { size: 24 };

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'; // gentle, modern ease-out

const KF = {
  popIn: {
    '0%': { opacity: 0, transform: 'scale(0.5) translateY(8px)' },
    '60%': { opacity: 1, transform: 'scale(1.06) translateY(0)' },
    '100%': { opacity: 1, transform: 'scale(1)' },
  },
  fadeUp: {
    '0%': { opacity: 0, transform: 'translateY(10px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
  },
  haloPulse: {
    '0%': { boxShadow: `0 0 0 0 ${PRIMARY_LIGHT_RGBA_14}` },
    '70%': { boxShadow: '0 0 0 10px rgba(0,0,0,0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(0,0,0,0)' },
  },
  dotPulse: {
    '0%, 100%': {
      transform: 'scale(0.85)',
      opacity: 0.6,
      boxShadow: '0 0 0 0 rgba(124,255,178,0.5)',
    },
    '50%': { transform: 'scale(1)', opacity: 1, boxShadow: '0 0 0 5px rgba(124,255,178,0)' },
  },
  typingBounce: {
    '0%, 80%, 100%': { transform: 'translateY(0)', opacity: 0.35 },
    '40%': { transform: 'translateY(-4px)', opacity: 1 },
  },
  // Moving light sweep across the thinking word, Claude-style.
  shimmer: {
    '0%': { backgroundPosition: '120% 0' },
    '100%': { backgroundPosition: '-20% 0' },
  },
  // Soft swap between thinking phrases.
  wordIn: {
    '0%': { opacity: 0, transform: 'translateY(3px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
  },
};

const styles = StyleSheet.create({
  launcher: {
    position: 'fixed',
    right: 24,
    bottom: 24,
    width: 58,
    height: 58,
    borderRadius: RADIUS_FULL,
    background: `linear-gradient(135deg, ${ACCENT_VIVID}, ${ACCENT_VIVID_DARK})`,
    border: 'none',
    cursor: 'pointer',
    zIndex: 1200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(76, 29, 149, 0.32)',
    animationName: [KF.popIn, KF.haloPulse],
    animationDuration: '0.45s, 2.6s',
    animationDelay: '0s, 0.6s',
    animationTimingFunction: `${EASE}, ease-out`,
    animationIterationCount: '1, infinite',
    transition: `transform 0.2s ${EASE}, box-shadow 0.2s ${EASE}`,
    ':hover': {
      transform: 'translateY(-2px) scale(1.05)',
      boxShadow: '0 12px 30px rgba(76, 29, 149, 0.42)',
    },
    ':active': { transform: 'translateY(0) scale(0.97)' },
  },
  panel: {
    position: 'fixed',
    right: 24,
    bottom: 24,
    width: 500,
    maxWidth: 'calc(100vw - 32px)',
    height: 'min(820px, calc(100vh - 48px))',
    background: BG_1,
    border: `1px solid ${BORDER_1}`,
    borderRadius: 20,
    overflow: 'hidden',
    zIndex: 1300,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: FONT_BODY,
    boxShadow: '0 24px 60px rgba(17, 12, 46, 0.26), 0 6px 16px rgba(17, 12, 46, 0.10)',
    transformOrigin: 'bottom right',
    transition: [
      `transform 0.34s ${EASE}`,
      `opacity 0.26s ${EASE}`,
      `width 0.3s ${EASE}`,
      `height 0.3s ${EASE}`,
    ].join(', '),
    willChange: 'transform, opacity, width, height',
  },
  panelOpen: { transform: 'translateY(0) scale(1)', opacity: 1, pointerEvents: 'auto' },
  panelClosed: { transform: 'translateY(12px) scale(0.92)', opacity: 0, pointerEvents: 'none' },
  // Maximize: dock to (roughly) the right half of the screen, near-full height.
  panelExpanded: {
    width: '50vw',
    minWidth: 480,
    height: 'calc(100vh - 32px)',
    right: 16,
    bottom: 16,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${SPACE_3}px ${SPACE_4}px`,
    background: `linear-gradient(135deg, ${ACCENT_VIVID}, ${ACCENT_VIVID_DARK})`,
    color: FG_INV,
    boxShadow: '0 2px 12px rgba(17, 12, 46, 0.12)',
    zIndex: 1,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE_2,
    fontFamily: FONT_TITLE,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    fontSize: 16,
    letterSpacing: 0.2,
    color: WHITE,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: RADIUS_FULL,
    background: '#7CFFB2',
    animationName: KF.dotPulse,
    animationDuration: '2.4s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
  },
  beta: {
    fontSize: FONT_SIZE_XS,
    fontWeight: FONT_WEIGHT_MEDIUM,
    opacity: 0.9,
    border: `1px solid rgba(255,255,255,0.5)`,
    background: 'rgba(255,255,255,0.12)',
    borderRadius: RADIUS_FULL,
    padding: '1px 7px',
    marginLeft: 4,
  },
  headerActions: { display: 'flex', alignItems: 'center', gap: 2 },
  persona: {
    display: 'flex',
    background: 'rgba(255,255,255,0.16)',
    borderRadius: RADIUS_FULL,
    padding: 2,
    marginRight: 4,
  },
  personaOpt: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'rgba(255,255,255,0.85)',
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_XS,
    fontWeight: FONT_WEIGHT_MEDIUM,
    borderRadius: RADIUS_FULL,
    padding: '2px 9px',
    transition: 'background 0.15s, color 0.15s',
    ':hover': { color: WHITE },
  },
  personaOptOn: { background: WHITE, color: ACCENT_VIVID_DARK, fontWeight: FONT_WEIGHT_SEMIBOLD },
  iconBtn: {
    background: 'transparent',
    border: 'none',
    color: WHITE,
    cursor: 'pointer',
    padding: 6,
    borderRadius: RADIUS_FULL,
    display: 'flex',
    opacity: 0.85,
    transition: `background 0.15s, opacity 0.15s, transform 0.15s ${EASE}`,
    ':hover': { background: 'rgba(255,255,255,0.16)', opacity: 1 },
  },
  iconBtnClose: {
    ':hover': { background: 'rgba(255,255,255,0.16)', opacity: 1, transform: 'rotate(90deg)' },
  },
  iconBtnOn: { background: 'rgba(255,255,255,0.20)', opacity: 1 },
  wikiWrap: { position: 'relative', display: 'flex' },
  wikiMenu: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    right: 0,
    zIndex: 20,
    minWidth: 240,
    maxWidth: 300,
    background: BG_1,
    border: `1px solid ${BORDER_1}`,
    borderRadius: RADIUS_MD,
    boxShadow: '0 8px 28px rgba(24,16,46,0.18)',
    padding: SPACE_2,
    animationName: KF.fadeUp,
    animationDuration: '0.18s',
    animationTimingFunction: EASE,
    animationFillMode: 'both',
  },
  wikiMenuHead: {
    color: FG_3,
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_XS,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    padding: `${SPACE_2} ${SPACE_2} 4px`,
  },
  wikiEmpty: { color: FG_3, fontFamily: FONT_BODY, fontSize: FONT_SIZE_SMALL, padding: SPACE_2 },
  wikiItem: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    borderRadius: RADIUS_SM,
    padding: `${SPACE_2} ${SPACE_2}`,
    transition: 'background 0.12s',
    ':hover': { background: BG_3 },
  },
  wikiItemTitle: {
    display: 'block',
    color: FG_1,
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
  },
  wikiItemDesc: {
    display: 'block',
    color: FG_2,
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_XS,
    marginTop: 2,
  },
  messages: { flex: 1, overflowY: 'auto', padding: SPACE_4, background: BG_2 },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: SPACE_2,
    marginBottom: SPACE_3,
    animationName: KF.fadeUp,
    animationDuration: '0.32s',
    animationTimingFunction: EASE,
    animationFillMode: 'both',
  },
  rowUser: { justifyContent: 'flex-end' },
  rowBot: { justifyContent: 'flex-start' },
  avatar: {
    flexShrink: 0,
    width: 28,
    height: 28,
    marginTop: 2,
    borderRadius: RADIUS_FULL,
    background: `linear-gradient(135deg, ${ACCENT_VIVID}, ${ACCENT_VIVID_DARK})`,
    color: WHITE,
    fontFamily: FONT_TITLE,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(76, 29, 149, 0.28)',
  },
  bubble: {
    maxWidth: '80%',
    minWidth: 0,
    padding: `${SPACE_2 + 1}px ${SPACE_3}px`,
    borderRadius: 16,
    fontSize: FONT_SIZE_BODY,
    lineHeight: 1.55,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  bubbleUser: {
    background: `linear-gradient(135deg, ${ACCENT_VIVID}, ${ACCENT_VIVID_DARK})`,
    color: WHITE,
    borderBottomRightRadius: RADIUS_MD,
    boxShadow: '0 3px 10px rgba(76, 29, 149, 0.22)',
  },
  bubbleBot: {
    background: BG_1,
    color: FG_1,
    borderTopLeftRadius: RADIUS_MD,
    boxShadow: '0 1px 3px rgba(17, 12, 46, 0.07), 0 6px 16px rgba(17, 12, 46, 0.04)',
  },
  bubbleWide: { flexGrow: 1, maxWidth: 'none' },
  explored: { marginTop: SPACE_2 },
  exploredToggle: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    color: FG_3,
    fontSize: FONT_SIZE_XS,
    fontFamily: FONT_BODY,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    ':hover': { color: FG_1 },
  },
  exploredChev: { display: 'inline-block', width: 10 },
  traceItem: { marginTop: SPACE_2 },
  traceHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontSize: FONT_SIZE_XS,
    color: FG_3,
  },
  traceOk: { color: SUCCESS_COLOR, fontWeight: FONT_WEIGHT_SEMIBOLD },
  traceFail: { color: WARNING_COLOR, fontWeight: FONT_WEIGHT_SEMIBOLD },
  traceMeta: { opacity: 0.8 },
  traceErr: { color: WARNING_COLOR, fontSize: FONT_SIZE_XS, marginTop: 2 },
  tracePre: {
    marginTop: 4,
    marginBottom: 0,
    background: BG_3,
    color: FG_2,
    borderRadius: RADIUS_SM,
    padding: SPACE_2,
    fontSize: FONT_SIZE_XS,
    lineHeight: 1.5,
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  },
  typing: { display: 'flex', gap: 5, padding: '4px 2px', alignItems: 'center' },
  tdot: {
    width: 7,
    height: 7,
    borderRadius: RADIUS_FULL,
    background: ACCENT_VIVID,
    animationName: KF.typingBounce,
    animationDuration: '1.3s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
  },
  tdot2: { animationDelay: '0.18s' },
  tdot3: { animationDelay: '0.36s' },
  inputBar: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE_2,
    padding: SPACE_3,
    borderTop: `1px solid ${BORDER_1}`,
    background: BG_1,
  },
  inputRow: { display: 'flex', alignItems: 'flex-end', gap: SPACE_2 },
  attachBtn: {
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: 14,
    border: `1px solid ${BORDER_1}`,
    background: 'transparent',
    color: FG_3,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: `color 0.15s, border-color 0.15s, background 0.15s, transform 0.12s ${EASE}`,
    ':hover': { color: ACCENT_VIVID, borderColor: ACCENT_VIVID },
    ':active': { transform: 'scale(0.94)' },
    ':disabled': { opacity: 0.4, cursor: 'not-allowed' },
  },
  attachBtnOn: { color: ACCENT_VIVID, borderColor: ACCENT_VIVID, background: BG_2 },
  attachErr: { fontSize: FONT_SIZE_XS, color: WARNING_COLOR },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    background: BG_2,
    border: `1px solid ${BORDER_1}`,
    borderRadius: RADIUS_MD,
    padding: '4px 8px',
  },
  chipOnBubble: {
    background: 'rgba(255,255,255,0.16)',
    border: '1px solid rgba(255,255,255,0.28)',
    marginBottom: 6,
  },
  chipThumb: { width: 32, height: 32, borderRadius: 7, objectFit: 'cover', display: 'block' },
  // Image rendered inside a sent user bubble — a tidy rounded thumbnail, click to zoom.
  bubbleImgBtn: {
    display: 'block',
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'zoom-in',
    marginBottom: 6,
    borderRadius: 12,
    overflow: 'hidden',
    lineHeight: 0,
    transition: `transform 0.12s ${EASE}`,
    ':hover': { transform: 'scale(1.02)' },
  },
  bubbleImg: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: 220,
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.25)',
  },
  lightbox: {
    position: 'fixed',
    inset: 0,
    zIndex: 1400,
    background: 'rgba(17, 12, 46, 0.82)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    cursor: 'zoom-out',
    animationName: { '0%': { opacity: 0 }, '100%': { opacity: 1 } },
    animationDuration: '0.18s',
    animationTimingFunction: EASE,
  },
  lightboxImg: {
    maxWidth: '92vw',
    maxHeight: '82vh',
    borderRadius: 12,
    boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
    cursor: 'default',
  },
  lightboxClose: {
    position: 'absolute',
    top: 18,
    right: 22,
    width: 40,
    height: 40,
    borderRadius: RADIUS_FULL,
    border: 'none',
    background: 'rgba(255,255,255,0.14)',
    color: WHITE,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': { background: 'rgba(255,255,255,0.26)' },
  },
  lightboxCaption: {
    marginTop: 14,
    color: 'rgba(255,255,255,0.85)',
    fontSize: FONT_SIZE_SMALL,
    fontFamily: FONT_BODY,
  },
  chipIcon: { fontSize: 16, lineHeight: 1 },
  chipName: {
    fontSize: FONT_SIZE_XS,
    maxWidth: 220,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chipRemove: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: FG_3,
    fontSize: 13,
    lineHeight: 1,
    padding: 2,
    ':hover': { color: WARNING_COLOR },
  },
  input: {
    flex: 1,
    resize: 'none',
    minHeight: 40,
    maxHeight: INPUT_MAX_H,
    overflowY: 'auto',
    border: `1px solid ${BORDER_1}`,
    borderRadius: 14,
    padding: `${SPACE_2 + 1}px ${SPACE_3}px`,
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_SMALL,
    lineHeight: 1.45,
    color: FG_1,
    outline: 'none',
    boxSizing: 'border-box',
    transition: `border-color 0.18s ${EASE}, box-shadow 0.18s ${EASE}`,
    ':focus': { borderColor: ACCENT_VIVID, boxShadow: `0 0 0 3px ${PRIMARY_LIGHT_RGBA_14}` },
    '::placeholder': { color: FG_3 },
  },
  sendBtn: {
    width: 40,
    height: 40,
    borderRadius: 14,
    border: 'none',
    background: `linear-gradient(135deg, ${ACCENT_VIVID}, ${ACCENT_VIVID_DARK})`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 3px 10px rgba(76, 29, 149, 0.24)',
    transition: `transform 0.16s ${EASE}, box-shadow 0.16s ${EASE}, opacity 0.16s`,
    ':hover': {
      transform: 'translateY(-1px) scale(1.04)',
      boxShadow: '0 5px 14px rgba(76, 29, 149, 0.34)',
    },
    ':active': { transform: 'scale(0.94)' },
  },
  sendBtnDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
    boxShadow: 'none',
    ':hover': { transform: 'none', boxShadow: 'none' },
  },
  // "Create shareable report" affordance under an answer, and the card it becomes.
  reportBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginTop: SPACE_3,
    padding: `${SPACE_2}px ${SPACE_3}px`,
    background: 'transparent',
    color: ACCENT_VIVID_DARK,
    border: `1px solid ${BORDER_1}`,
    borderRadius: RADIUS_FULL,
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_XS,
    fontWeight: FONT_WEIGHT_MEDIUM,
    cursor: 'pointer',
    transition: `background 0.16s ${EASE}, border-color 0.16s ${EASE}`,
    ':hover': { background: PRIMARY_LIGHT_RGBA_14, borderColor: ACCENT_VIVID },
    ':disabled': { opacity: 0.6, cursor: 'default' },
  },
  reportCard: {
    marginTop: SPACE_3,
    padding: `${SPACE_3}px`,
    background: PRIMARY_LIGHT_RGBA_14,
    border: `1px solid ${BORDER_1}`,
    borderRadius: RADIUS_MD,
  },
  reportHead: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: SPACE_2,
    marginBottom: SPACE_2,
  },
  reportReady: {
    fontSize: FONT_SIZE_SMALL,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    color: FG_1,
  },
  reportExpiry: { fontSize: FONT_SIZE_XS, color: FG_3 },
  reportActions: { display: 'flex', alignItems: 'center', gap: SPACE_2 },
  reportOpen: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: `${SPACE_2}px ${SPACE_3}px`,
    background: `linear-gradient(135deg, ${ACCENT_VIVID}, ${ACCENT_VIVID_DARK})`,
    color: WHITE,
    borderRadius: RADIUS_FULL,
    fontSize: FONT_SIZE_XS,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    textDecoration: 'none',
    transition: `transform 0.16s ${EASE}, box-shadow 0.16s ${EASE}`,
    ':hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(76, 29, 149, 0.3)' },
  },
  reportCopy: {
    padding: `${SPACE_2}px ${SPACE_3}px`,
    background: BG_1,
    color: FG_2,
    border: `1px solid ${BORDER_1}`,
    borderRadius: RADIUS_FULL,
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_XS,
    fontWeight: FONT_WEIGHT_MEDIUM,
    cursor: 'pointer',
    ':hover': { background: BG_2 },
  },
  // Cycling "thinking" word with a soft left-to-right light sweep.
  thinkingWord: {
    display: 'inline-block',
    fontSize: FONT_SIZE_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    backgroundImage: `linear-gradient(90deg, ${FG_3} 35%, ${FG_1} 50%, ${FG_3} 65%)`,
    backgroundSize: '220% 100%',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'transparent',
    animationName: [KF.shimmer, KF.wordIn],
    animationDuration: '1.6s, 0.3s',
    animationIterationCount: 'infinite, 1',
    animationTimingFunction: 'linear, ease-out',
  },
  // Suggested follow-up chips pinned just above the composer.
  suggestRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: SPACE_2,
    padding: `0 ${SPACE_4}px ${SPACE_2}px`,
  },
  suggestChip: {
    maxWidth: '100%',
    padding: `${SPACE_2}px ${SPACE_3}px`,
    background: BG_1,
    color: ACCENT_VIVID_DARK,
    border: `1px solid ${BORDER_1}`,
    borderRadius: RADIUS_FULL,
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_XS,
    fontWeight: FONT_WEIGHT_MEDIUM,
    textAlign: 'left',
    lineHeight: 1.3,
    cursor: 'pointer',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    transition: `background 0.16s ${EASE}, border-color 0.16s ${EASE}, transform 0.16s ${EASE}`,
    animationName: KF.fadeUp,
    animationDuration: '0.3s',
    animationTimingFunction: EASE,
    ':hover': {
      background: PRIMARY_LIGHT_RGBA_14,
      borderColor: ACCENT_VIVID,
      transform: 'translateY(-1px)',
    },
  },
});
